import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { withTranslation } from "react-i18next";
import { Modal, ModalBody, Spinner } from "reactstrap";
import crossIcon from "assets/images/cross.svg";
import Text from "components/Text";
import Empty from "components/Empty";
import ExpandableSearchBar from "accountOwner/pages/AddEvent/SchedulingAssistant/components/ExpandableSearchBar";
import addEventStyles from "accountOwner/pages/AddEvent/AddEvent.module.scss";
import { useGetAllSchedulerTemplates } from "repositories/scheduler-repository";
import { handleError } from "utils";
import {
  APPLY_TEMPLATE_PAGE_SIZE,
  APPLY_TEMPLATE_SEARCH_DEBOUNCE_MS,
  getApplyTemplatePeriodDetails,
  getApplyTemplateViewType,
  normalizeSchedulerTemplateList,
} from "../EventShiftsView/eventsWorkspaceApplyTemplate.utils";
import styles from "./PublishSchedulerEventsConfirmationModal.module.scss";
import listStyles from "./AddFromSavedTemplateModal.module.scss";

/**
 * Modal to pick a saved scheduler template and apply it to the current period.
 * Matches the Add from Saved Template design (search + radio list + week dates).
 */
function AddFromSavedTemplateModal({
  t,
  isOpen,
  isLoading,
  calendarViewId,
  currentDate,
  ownerId,
  onConfirm,
  onClose,
}) {
  const periodDetails = useMemo(
    () => getApplyTemplatePeriodDetails(calendarViewId, currentDate),
    [calendarViewId, currentDate],
  );
  const viewType = useMemo(
    () => getApplyTemplateViewType(calendarViewId),
    [calendarViewId],
  );

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const lastReportedErrorRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setSearchInput("");
    setDebouncedSearch("");
    setSelectedTemplateId(null);
    lastReportedErrorRef.current = null;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, APPLY_TEMPLATE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, isOpen]);

  const {
    data: templatesResponse,
    isLoading: isTemplatesLoading,
    isFetching,
    isError,
    error,
  } = useGetAllSchedulerTemplates({
    ownerId,
    viewType,
    nameSearch: debouncedSearch,
    pageNumber: 1,
    pageSize: APPLY_TEMPLATE_PAGE_SIZE,
    enabled: isOpen && Boolean(ownerId),
  });

  const templates = useMemo(
    () => (isError ? [] : normalizeSchedulerTemplateList(templatesResponse)),
    [templatesResponse, isError],
  );

  useEffect(() => {
    if (!isOpen || !isError) {
      if (!isError) lastReportedErrorRef.current = null;
      return;
    }
    const message = error?.message || String(error || "");
    if (lastReportedErrorRef.current === message) return;
    lastReportedErrorRef.current = message;
    handleError(message || error);
    setSelectedTemplateId(null);
  }, [isOpen, isError, error]);

  useEffect(() => {
    if (!selectedTemplateId) return;
    const stillVisible = templates.some(
      (item) => item.id === selectedTemplateId,
    );
    if (!stillVisible) {
      setSelectedTemplateId(null);
    }
  }, [templates, selectedTemplateId]);

  const showListLoader =
    isTemplatesLoading || (isFetching && !templates.length);
  const showEmpty = !showListLoader && !isError && !templates.length;

  const handleUseTemplate = useCallback(async () => {
    if (isLoading || selectedTemplateId == null) return;
    await onConfirm?.(selectedTemplateId);
  }, [isLoading, selectedTemplateId, onConfirm]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={isLoading ? undefined : onClose}
      className="modal-dialog-centered modal-width-660"
      modalClassName="custom-modal"
    >
      <span className="close-btn" onClick={isLoading ? undefined : onClose}>
        <img src={crossIcon} alt="" />
      </span>
      <ModalBody className={styles["publish-modal-body"]}>
        <Text
          size="25px"
          marginBottom="16px"
          weight="500"
          align="left"
          color="#111b45"
        >
          {t(periodDetails.titleKey)}
        </Text>
        <p className={styles["publish-modal-message"]}>
          {t(periodDetails.introKey, { period: periodDetails.periodValue })}
        </p>

        <div className={`search-box w-100 ${addEventStyles["search-box"]}`}>
          <ExpandableSearchBar
            placeholder={t("scheduler.searchTemplateByName")}
            searchValue={searchInput}
            setSearchValue={(value) => {
              if (!isLoading) setSearchInput(value);
            }}
            expanded={true}
          />
        </div>

        {showListLoader && (
          <div className={listStyles["template-list-loader"]}>
            <Spinner animation="border" className="loader-spinner" />
          </div>
        )}

        {!showListLoader && templates.length > 0 && (
          <ul
            className={`modal-employee-list group-list radio-settings ${listStyles["template-radio-list"]}`}
            role="radiogroup"
            aria-label={t("scheduler.addFromSavedTemplate")}
          >
            {templates.map((item) => (
              <li key={item.id}>
                <div className={`${addEventStyles.border_list} ch-radio`}>
                  <label>
                    <input
                      type="radio"
                      name="schedulerSavedTemplate"
                      checked={selectedTemplateId === item.id}
                      disabled={isLoading}
                      onChange={() => setSelectedTemplateId(item.id)}
                    />
                    <span>{item.name}</span>
                  </label>
                </div>
              </li>
            ))}
          </ul>
        )}

        {showEmpty && (
          <Empty Message={t("scheduler.noTemplatesAvailableForSelection")} />
        )}

        <div className={styles["publish-modal-actions"]}>
          <button
            type="button"
            className="button button-round button-shadow"
            disabled={isLoading || selectedTemplateId == null}
            onClick={handleUseTemplate}
          >
            {t("scheduler.useTemplate")}
          </button>
          <button
            type="button"
            className="button button-round button-border button-dark"
            disabled={isLoading}
            onClick={onClose}
          >
            {t("cancel")}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
}

export default withTranslation()(AddFromSavedTemplateModal);
